// Интеграции: настройка 1С, проверка контрагента в Контур.Фокус, журнал синхронизации.
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plug, RefreshCw, Search, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { integrationsApi, getErrorMessage } from '@/api';
import { AppLayout, PageHeader } from '@/components/layout/AppLayout';
import { Spinner } from '@/components/ui/Spinner';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/components/ui/Toast';
import { formatDateTime } from '@/lib/format';
import type { OneCConnection, FocusResult } from '@/types';

export function IntegrationsPage() {
  const { toast } = useToast();
  const qc = useQueryClient();

  const { data: connections } = useQuery({ queryKey: ['1c-connections'], queryFn: () => integrationsApi.connections() });
  const { data: log } = useQuery({ queryKey: ['sync-log'], queryFn: () => integrationsApi.syncLog() });

  const conn: OneCConnection | undefined = connections?.[0];
  const [wsUrl, setWsUrl] = useState('');
  const [wsUser, setWsUser] = useState('');
  const [wsPassword, setWsPassword] = useState('');

  // Подставляем существующие значения
  const effectiveUrl = wsUrl || conn?.ws_url || '';
  const effectiveUser = wsUser || conn?.ws_user || '';

  const saveM = useMutation({
    mutationFn: () => integrationsApi.saveConnection({
      id: conn?.id, ws_url: effectiveUrl, ws_user: effectiveUser,
      ...(wsPassword ? { ws_password: wsPassword } : {}),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['1c-connections'] }); setWsPassword(''); toast('Настройки 1С сохранены', 'success'); },
    onError: (e) => toast(getErrorMessage(e), 'error'),
  });
  const syncM = useMutation({
    mutationFn: () => integrationsApi.sync(conn!.id),
    onSuccess: (d) => { toast(d.detail, 'success'); qc.invalidateQueries({ queryKey: ['sync-log'] }); },
    onError: (e) => toast(getErrorMessage(e), 'error'),
  });

  return (
    <AppLayout>
      <PageHeader title="Интеграции" subtitle="1С, Контур.Фокус и синхронизация данных" />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, alignItems: 'start' }}>
        {/* 1С */}
        <div className="panel">
          <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, fontSize: 15 }}>
            <Plug size={18} style={{ color: 'var(--accent)' }} /> Подключение к 1С
          </h3>
          <div className="field">
            <label className="field__label">URL Web-сервиса 1С</label>
            <input className="field__input" value={effectiveUrl} onChange={(e) => setWsUrl(e.target.value)}
              placeholder="https://1c.company.ru/ws/Exchange" />
          </div>
          <div className="field">
            <label className="field__label">Логин</label>
            <input className="field__input" value={effectiveUser} onChange={(e) => setWsUser(e.target.value)} />
          </div>
          <div className="field">
            <label className="field__label">Пароль {conn && '(оставьте пустым, чтобы не менять)'}</label>
            <input type="password" className="field__input" value={wsPassword} onChange={(e) => setWsPassword(e.target.value)} />
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <Button variant="primary" disabled={!effectiveUrl || saveM.isPending} onClick={() => saveM.mutate()}>
              {saveM.isPending ? 'Сохранение…' : 'Сохранить'}
            </Button>
            {conn && (
              <Button disabled={syncM.isPending} onClick={() => syncM.mutate()}>
                <RefreshCw size={15} /> {syncM.isPending ? 'Запуск…' : 'Синхронизировать'}
              </Button>
            )}
          </div>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 12 }}>
            Выгрузка актов КС-2/КС-3 и контрагентов в 1С. Формат обмена зависит от
            конфигурации 1С клиента.
          </p>
        </div>

        {/* Контур.Фокус */}
        <FocusCheck />
      </div>

      {/* Журнал синхронизации */}
      <div className="panel" style={{ marginTop: 20 }}>
        <h3 style={{ marginBottom: 14, fontSize: 15 }}>Журнал синхронизации</h3>
        {!log || log.length === 0 ? (
          <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Записей пока нет</p>
        ) : (
          <table className="data-table">
            <thead><tr><th>Источник</th><th>Статус</th><th>Сообщение</th><th>Время</th></tr></thead>
            <tbody>
              {log.map((e) => (
                <tr key={e.id}>
                  <td>{e.source}</td>
                  <td>{e.status}</td>
                  <td style={{ color: 'var(--text-secondary)' }}>{e.message}</td>
                  <td className="mono">{formatDateTime(e.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AppLayout>
  );
}

function FocusCheck() {
  const { toast } = useToast();
  const [inn, setInn] = useState('');
  const [result, setResult] = useState<FocusResult | null>(null);
  const [notConfigured, setNotConfigured] = useState(false);

  const m = useMutation({
    mutationFn: () => integrationsApi.focusVerify(inn),
    onSuccess: (r) => { setResult(r); setNotConfigured(false); },
    onError: (e: unknown) => {
      const err = e as { response?: { status?: number; data?: FocusResult } };
      if (err.response?.status === 503) {
        setNotConfigured(true);
        setResult(null);
      } else {
        toast(getErrorMessage(e), 'error');
      }
    },
  });

  return (
    <div className="panel">
      <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, fontSize: 15 }}>
        <Search size={18} style={{ color: 'var(--accent)' }} /> Проверка контрагента (Контур.Фокус)
      </h3>
      <div className="field">
        <label className="field__label">ИНН контрагента</label>
        <div style={{ display: 'flex', gap: 8 }}>
          <input className="field__input" value={inn} onChange={(e) => setInn(e.target.value)}
            placeholder="7701234567" inputMode="numeric" />
          <Button variant="primary" disabled={!inn || m.isPending} onClick={() => m.mutate()}>
            {m.isPending ? 'Проверка…' : 'Проверить'}
          </Button>
        </div>
      </div>

      {notConfigured && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: 12, background: 'rgba(240,201,74,0.1)', borderRadius: 'var(--radius-sm)', marginTop: 8 }}>
          <AlertTriangle size={18} style={{ color: 'var(--yellow)', flexShrink: 0 }} />
          <div style={{ fontSize: 13 }}>
            Интеграция с Контур.Фокус не настроена. Добавьте ключ <code>KONTUR_FOCUS_API_KEY</code>
            в переменные окружения сервера.
          </div>
        </div>
      )}

      {result && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: 12, background: 'var(--bg-card)', borderRadius: 'var(--radius-sm)', marginTop: 8 }}>
          {result.status === 'active'
            ? <CheckCircle size={18} style={{ color: 'var(--green)', flexShrink: 0 }} />
            : <XCircle size={18} style={{ color: 'var(--red)', flexShrink: 0 }} />}
          <div style={{ fontSize: 13 }}>
            <div style={{ fontWeight: 500 }}>{result.name || 'Контрагент'}</div>
            <div style={{ color: 'var(--text-secondary)' }}>
              Статус: {result.status === 'active' ? 'Действующая организация' : 'Недействующая / требует внимания'}
            </div>
          </div>
        </div>
      )}

      <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 12 }}>
        Проверка надёжности контрагента по ИНН перед заключением договора.
      </p>
    </div>
  );
}
